// src/pages/angebote/CustomerReviews.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, Button,
  Typography, CircularProgress, Alert, useMediaQuery, useTheme, Card, Avatar,
  CardContent, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, TablePagination
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import tripLogo   from '../../assets/TripAdvisor_Logo.svg.png';
import googleLogo from '../../assets/Google_2015_logo.svg.png';

// ─── Inline-SVG Sterne ───────────────────────────────────────────────────────
const StarIcon = React.memo(() => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="#FFA726">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
));
const StarBorderIcon = React.memo(() => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="#FFA726">
    <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
  </svg>
));

// ─── Konstanten ──────────────────────────────────────────────────────────────
const statusColors = {
  Angefragt:        'info',
  'In Bearbeitung': 'warning',
  Rückmeldung:      'secondary',
  Erfolgreich:      'success',
  'Kein Verstoß':   'error',
};
const platformLogos = { Tripadvisor: tripLogo, Google: googleLogo };
const TEXT_LIMIT    = 120;
const API_URL       = process.env.REACT_APP_API_URL || '';

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
}

const formatDate = date =>
  date
    ? new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

const renderStars = count => {
  const full  = Math.min(5, Math.max(0, Math.floor(count || 0)));
  const empty = 5 - full;
  return (
    <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
      {[...Array(full)].map((_, i)  => <StarIcon key={i} />)}
      {[...Array(empty)].map((_, i) => <StarBorderIcon key={i} />)}
    </Box>
  );
};

const renderObject = obj =>
  obj && typeof obj === 'object'
    ? Object.entries(obj)
        .filter(([, v]) => v != null && v !== 0)
        .map(([k, v]) => <Typography key={k} variant="body2">{k}: {v}</Typography>)
    : null;

const hasDetails = r =>
  Boolean(
    r.responseFromOwnerText ||
    r.reviewImageUrls?.length ||
    (r.reviewContext        && Object.values(r.reviewContext).some(v => v)) ||
    (r.reviewDetailedRating && Object.values(r.reviewDetailedRating).some(v => v))
  );

// ─── Desktop-Zeile ───────────────────────────────────────────────────────────
const ReviewRow = React.memo(({ review, expanded, toggleExpand, selected, toggleSelect, openDetails }) => {
  const text     = review.text || '';
  const isLong   = text.length > TEXT_LIMIT;
  const shown    = expanded[review.b_id] ? text : text.slice(0, TEXT_LIMIT);
  const disabled = review.b_status !== 'neu';

  return (
    <TableRow
      hover
      onClick={disabled ? undefined : () => toggleSelect(review.b_id)}
      selected={!!selected[review.b_id]}
      sx={{ cursor: disabled ? 'default' : 'pointer', bgcolor: selected[review.b_id] ? 'action.selected' : undefined }}
    >
      <TableCell padding="checkbox">
        {review.b_status === 'neu' && (
          <Checkbox
            checked={!!selected[review.b_id]}
            onChange={() => toggleSelect(review.b_id)}
            onClick={e => e.stopPropagation()}
          />
        )}
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
          <Avatar src={review.reviewerPhotoUrl} sx={{ width: 45, height: 45 }} />
          {review.name}
        </Box>
      </TableCell>
      <TableCell sx={{ px: 1 }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          {platformLogos[review.b_plattform]
            ? <Avatar src={platformLogos[review.b_plattform]} sx={{ width: 25, height: 25 }} />
            : <Chip label={review.b_plattform || '-'} size="small" />}
          {renderStars(review.rating)}
        </Box>
      </TableCell>
      <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal' }}>
        <Typography variant="caption" fontWeight="bold" display="block">
          {formatDate(review.publishedAtDate)}
        </Typography>
        {shown}{isLong && ' '}
        {isLong && (
          <Button size="small" onClick={e => { e.stopPropagation(); toggleExpand(review.b_id); }}>
            {expanded[review.b_id] ? 'weniger' : 'mehr..'}
          </Button>
        )}
      </TableCell>
      <TableCell>
        {hasDetails(review) && (
          <IconButton size="small" onClick={e => { e.stopPropagation(); openDetails(review); }}>
            <InfoIcon />
          </IconButton>
        )}
      </TableCell>
      <TableCell>
        {review.b_status !== 'neu' && (
          <Chip label={review.b_status} size="small" color={statusColors[review.b_status] || 'default'} />
        )}
      </TableCell>
    </TableRow>
  );
});

// ─── Hauptkomponente ─────────────────────────────────────────────────────────
const CustomerReviews = () => {
  const { linkToken }  = useParams();
  const theme          = useTheme();
  const isSm           = useMediaQuery(theme.breakpoints.down('sm'));

  const [company,      setCompany]      = useState(null);
  const [placeId,      setPlaceId]      = useState('');
  const [reviews,      setReviews]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [errorPlace,   setErrorPlace]   = useState('');
  const [searchTerm,   setSearchTerm]   = useState('');
  const debounceSearch = useDebounce(searchTerm, 300);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [selected,     setSelected]     = useState({});
  const [deleting,     setDeleting]     = useState(false);
  const [expanded,     setExpanded]     = useState({});
  const [openModal,    setOpenModal]    = useState(false);
  const [modalData,    setModalData]    = useState({});
  const [successOpen,  setSuccessOpen]  = useState(false);
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(10);
  const [submitError,  setSubmitError]  = useState('');

  const selectedCount = useMemo(
    () => Object.values(selected).filter(v => v).length,
    [selected]
  );
  const barHeight = 80;

  // ── Firmeninfo laden (kein API-Key nötig – Link-Token reicht) ──
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/angebote/${linkToken}`)
      .then(res => {
        setPlaceId(res.data.place_id);
        setCompany({
          imageUrl:    res.data.imageUrl,
          name:        res.data.firmenname,
          address:     res.data.adresse,
          email:       res.data.email,
          avgRating:   res.data.averageRating,
          reviewCount: res.data.reviewCount,
        });
        setErrorPlace('');
      })
      .catch(() => setErrorPlace(
        'Ihr einmaliger Link ist abgelaufen oder ungültig. Bitte fordern Sie über das Formular einen neuen an.'
      ))
      .finally(() => setLoading(false));
  }, [linkToken]);

  // ── Link-Öffnung tracken ──
  useEffect(() => {
    if (!placeId) return;
    axios
      .post(`${API_URL}/api/angebote/link-geoeffnet`, { place_id: placeId })
      .catch(console.error);
  }, [placeId]);

  // ── Bewertungen laden (via Token – kein API-Key im Frontend) ──
  const loadReviews = useCallback(() => {
    if (!linkToken) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/reviews/by-token/${linkToken}`)
      .then(res => {
        const withStatus = res.data.map(r => ({
          ...r,
          b_status: r.b_status ?? r.status ?? 'neu',
        }));
        setReviews(withStatus);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [linkToken]);

  // Initialer Fetch + Polling alle 30 Sekunden
  useEffect(() => {
    if (!placeId) return;
    loadReviews();
    const interval = setInterval(loadReviews, 30000);
    return () => clearInterval(interval);
  }, [placeId, loadReviews]);

  // ── Filter & Pagination ──
  const filtered = useMemo(() =>
    reviews.filter(r => {
      const txt = `${r.name || ''} ${r.text || ''}`.toLowerCase();
      return (
        txt.includes(debounceSearch.toLowerCase()) &&
        (ratingFilter === 'all' || r.rating === +ratingFilter)
      );
    }),
  [reviews, debounceSearch, ratingFilter]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  // ── Callbacks ──
  const toggleSelect = useCallback(id => {
    const rev = reviews.find(r => r.b_id === id);
    if (!rev || rev.b_status !== 'neu') return;
    setSelected(s => ({ ...s, [id]: !s[id] }));
  }, [reviews]);

  const newOnes     = filtered.filter(r => r.b_status === 'neu');
  const allSelected = newOnes.length > 0 && newOnes.every(r => selected[r.b_id]);

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelected({});
    } else {
      const sel = {};
      newOnes.forEach(r => { sel[r.b_id] = true; });
      setSelected(sel);
    }
  }, [newOnes, allSelected]);

  const toggleExpand = useCallback(id => setExpanded(e => ({ ...e, [id]: !e[id] })), []);
  const openDetails  = useCallback(r => { setModalData(r); setOpenModal(true); }, []);
  const closeDetails = useCallback(() => setOpenModal(false), []);

  const handleChangePage          = useCallback((_, p) => setPage(p), []);
  const handleChangeRowsPerPage   = useCallback(e => { setRowsPerPage(+e.target.value); setPage(0); }, []);

  // ── Löschanfrage absenden (Authentifizierung via link_token im Body) ──
  const handleDeleteRequest = useCallback(async () => {
    // IDs als Strings für den Vergleich und als Numbers für den API-Call
    const idStrings = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    const idNumbers = idStrings.map(Number);
    if (!idNumbers.length) return;
    setDeleting(true);
    setSubmitError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/review-requests`,
        { link_token: linkToken, place_id: placeId, review_ids: idNumbers }
      );
      if (data.success) {
        // b_id aus DB kann number oder string sein – String-Vergleich ist sicher
        setReviews(prev =>
          prev.map(r =>
            idStrings.includes(String(r.b_id))
              ? { ...r, b_status: 'Angefragt' }
              : r
          )
        );
        setSelected({});
        setSuccessOpen(true);
        loadReviews();
      } else {
        setSubmitError('Die Anfrage konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
      }
    } catch (err) {
      console.error('Fehler beim Senden der Löschanfrage:', err);
      const msg = err.response?.data?.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
      setSubmitError(msg);
    } finally {
      setDeleting(false);
    }
  }, [selected, placeId, linkToken, loadReviews]);

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading && !company) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (errorPlace) {
    return (
      <Box p={3}>
        <Alert severity="error">{errorPlace}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>

      {/* Firmen-Header */}
      {company && (
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          {company.imageUrl && (
            <Avatar src={company.imageUrl} variant="rounded" sx={{ width: 60, height: 60 }} />
          )}
          <Box>
            <Typography variant="h6" fontWeight="bold">{company.name}</Typography>
            <Typography variant="body2" color="text.secondary">{company.address}</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {renderStars(company.avgRating)}
              <Typography variant="body2">({company.reviewCount} Bewertungen)</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Filter-Zeile */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
        <TextField
          label="Suche"
          size="small"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sterne</InputLabel>
          <Select value={ratingFilter} label="Sterne" onChange={e => setRatingFilter(e.target.value)}>
            <MenuItem value="all">Alle</MenuItem>
            {[1, 2, 3, 4, 5].map(n => (
              <MenuItem key={n} value={n}>{n} ★</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Mobile View */}
      {!loading && isSm && (
        <Box sx={{ mb: selectedCount > 0 ? `${barHeight}px` : 0 }}>
          <Grid container spacing={2}>
            {paged.map(r => (
              <Grid item xs={12} key={r.b_id}>
                <Card
                  elevation={selected[r.b_id] ? 4 : 1}
                  sx={{
                    position: 'relative',
                    cursor: r.b_status === 'neu' ? 'pointer' : 'default',
                    border: selected[r.b_id] ? '2px solid' : '1px solid transparent',
                    borderColor: selected[r.b_id] ? 'primary.main' : 'transparent',
                  }}
                  onClick={r.b_status === 'neu' ? () => toggleSelect(r.b_id) : undefined}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Avatar src={r.reviewerPhotoUrl} sx={{ width: 40, height: 40 }} />
                      <Box flexGrow={1}>
                        <Typography fontWeight="bold" variant="body2">{r.name}</Typography>
                        {renderStars(r.rating)}
                      </Box>
                      {r.b_status === 'neu' && (
                        <Checkbox
                          checked={!!selected[r.b_id]}
                          onChange={() => toggleSelect(r.b_id)}
                          onClick={e => e.stopPropagation()}
                          sx={{ transform: 'scale(1.4)' }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" fontWeight="bold" sx={{ mt: 1, mb: 1, display: 'block' }}>
                      {formatDate(r.publishedAtDate)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {expanded[r.b_id] ? r.text : r.text?.slice(0, TEXT_LIMIT)}
                      {r.text?.length > TEXT_LIMIT && (
                        <Button size="small" onClick={e => { e.stopPropagation(); toggleExpand(r.b_id); }}>
                          {expanded[r.b_id] ? 'weniger' : 'mehr...'}
                        </Button>
                      )}
                    </Typography>
                    {r.b_status !== 'neu' && (
                      <Box sx={{ position: 'absolute', bottom: 8, left: 14 }}>
                        <Chip label={r.b_status} size="small" color={statusColors[r.b_status] || 'default'} />
                      </Box>
                    )}
                    {hasDetails(r) && (
                      <IconButton
                        size="small"
                        onClick={e => { e.stopPropagation(); openDetails(r); }}
                        sx={{ position: 'absolute', bottom: 8, right: 14 }}
                      >
                        <InfoIcon fontSize="large" />
                      </IconButton>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}

            <Grid item xs={12}>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Zeilen:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Desktop View */}
      {!loading && !isSm && (
        <Box sx={{ width: '100%', overflowX: 'auto', mb: selectedCount > 0 ? `${barHeight}px` : 0 }}>
          <TableContainer component={Paper} sx={{ minWidth: 1200 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && Object.values(selected).some(v => v)}
                      onChange={toggleSelectAll}
                      disabled={loading}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 180 }}>Von</TableCell>
                  <TableCell sx={{ px: 1, width: 110 }}>Plattform / Sterne</TableCell>
                  <TableCell>Text</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map(r => (
                  <ReviewRow
                    key={r.b_id}
                    review={r}
                    expanded={expanded}
                    toggleExpand={toggleExpand}
                    selected={selected}
                    toggleSelect={toggleSelect}
                    openDetails={openDetails}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Zeilen pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
          />
        </Box>
      )}

      {/* Fehler-Anzeige */}
      {submitError && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert severity="error" onClose={() => setSubmitError('')}>{submitError}</Alert>
        </Box>
      )}

      {/* Floating Action Bar (wenn Bewertungen ausgewählt) */}
      {selectedCount > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            height: `${barHeight}px`,
            bgcolor: '#f5fff9',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: '25px',
            zIndex: theme => theme.zIndex.drawer + 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: '#444' }}>
            {selectedCount} Bewertung(en) ausgewählt
          </Typography>
          <Button variant="contained" color="success" onClick={handleDeleteRequest} disabled={deleting}>
            {deleting ? 'Bitte warten…' : 'Löschanfrage stellen'}
          </Button>
        </Paper>
      )}

      {/* Erfolgs-Dialog */}
      <Dialog open={successOpen} onClose={() => { setSuccessOpen(false); loadReviews(); }}>
        <DialogTitle>Erfolgreich übermittelt</DialogTitle>
        <DialogContent>
          <Typography>Die Löschanforderung wurde erfolgreich übermittelt.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Details-Dialog */}
      <Dialog open={openModal} onClose={closeDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Details zur Bewertung</DialogTitle>
        <DialogContent dividers>
          {modalData.responseFromOwnerText && (
            <>
              <Typography variant="subtitle2">Antwort vom Inhaber:</Typography>
              <Typography paragraph>{modalData.responseFromOwnerText}</Typography>
            </>
          )}
          {modalData.reviewImageUrls?.length > 0 && (
            <>
              <Typography variant="subtitle2">Bilder:</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {modalData.reviewImageUrls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <Avatar variant="square" src={url} sx={{ width: 60, height: 60 }} />
                  </a>
                ))}
              </Box>
            </>
          )}
          {renderObject(modalData.reviewContext) && (
            <>
              <Typography variant="subtitle2">Reiseart / Kontext:</Typography>
              <Box mb={2}>{renderObject(modalData.reviewContext)}</Box>
            </>
          )}
          {renderObject(modalData.reviewDetailedRating) && (
            <>
              <Typography variant="subtitle2">Detailbewertung:</Typography>
              <Box>{renderObject(modalData.reviewDetailedRating)}</Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerReviews;